import { BrowserModule } from "@angular/platform-browser";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpModule } from "@angular/http";
import { NgModule, ApplicationRef } from "@angular/core";
import {
  removeNgStyles,
  createNewHosts,
  createInputTransfer,
} from "@angularclass/hmr";
import { RouterModule, PreloadAllModules } from "@angular/router";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

/*
 * Platform and Environment providers/directives/pipes
 */
import { ENV_PROVIDERS } from "./environment";
import { ROUTES } from "./app.routes";

// npm installs
import { SlimLoadingBarModule } from "ng2-slim-loading-bar";
import { Ng2Bs3ModalModule } from "ng2-bs3-modal/ng2-bs3-modal";
import { SimpleNotificationsModule } from "angular2-notifications";
import { Cookie } from "ng2-cookies/ng2-cookies";
import { AgmCoreModule } from "@agm/core";

// App is our top level component
import { AppComponent } from "./app.component";
import { HeaderComponent } from "./header";
import { FooterComponent } from "./footer";
import { LoginComponent } from "./auth/login";
import { LogoutComponent } from "./auth/logout";
import { ForgotPasswordComponent } from "./auth/forgot-password";
import { ResetPasswordComponent } from "./auth/reset-password";
import { ChangePasswordComponent } from "./auth/change-password";
import { AdminService } from "./service/admin.service";
import { APP_RESOLVER_PROVIDERS } from "./app.resolver";
import { AppState, InternalStateType } from "./app.service";
import { NoContentComponent } from "./no-content";
import { DashboardComponent } from "./dashboard";
import { Dashboard1Component } from "./manage/dashboard1";
import { UserComponent } from "./manage/users";
import { TicketComponent } from "./manage/ticket";
import { CreateTicketComponent } from "./manage/create-ticket";
import { CustomModalsComponent } from "./custom-modals";
import { AuthGuard } from "./service/auth-guard.service";
import { PagerService } from "./pipe/pagination";
import { MyDatePickerModule } from "mydatepicker";
import { DateTimePickerModule } from "ng-pick-datetime";
import { FormatDatePipe } from "./pipe/format-date.pipe";
import { KeysPipe } from "./pipe/keys.pipe";
import { GOOGLE_MAPS_JS_API_KEY } from "./app.constants";

import "../styles/styles.scss";
import "../styles/headings.css";
import { TicketLogComponent } from "./manage/ticket-log/ticket-log.component";
import { ReportedSellersComponent } from "./manage/reported-sellers/reported-sellers.component";
import { FlagTicketTresholdCountComponent } from "./manage/flag-ticket-treshold-count/flag-ticket-treshold-count.component";

import { ChartsModule } from "ng2-charts";
import { ParseTicketsComponent } from "./manage/parse-tickets/parse-tickets.component";
import { ProvidersComponent } from "./manage/providers/providers.component";

// Application wide providers
const APP_PROVIDERS = [
  ...APP_RESOLVER_PROVIDERS,
  AppState,
  AdminService,
  AuthGuard,
  PagerService,
];

type StoreType = {
  state: InternalStateType;
  restoreInputValues: () => void;
  disposeOldHosts: () => void;
};

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    LoginComponent,
    LogoutComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    ChangePasswordComponent,
    DashboardComponent,
    Dashboard1Component,
    UserComponent,
    TicketComponent,
    TicketLogComponent,
    CreateTicketComponent,
    ParseTicketsComponent,
    ProvidersComponent,
    ReportedSellersComponent,
    FlagTicketTresholdCountComponent,
    FormatDatePipe,
    KeysPipe,
    NoContentComponent,
    CustomModalsComponent,
  ],
  /**
   * Import Angular's modules.
   */
  imports: [
    AgmCoreModule.forRoot({
      apiKey: GOOGLE_MAPS_JS_API_KEY,
      libraries: ["places"],
    }),
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpModule,
    SlimLoadingBarModule.forRoot(),
    SimpleNotificationsModule.forRoot(),
    Ng2Bs3ModalModule,
    MyDatePickerModule,
    DateTimePickerModule,
    RouterModule.forRoot(ROUTES, {
      useHash: false,
      preloadingStrategy: PreloadAllModules,
    }),
    ChartsModule,
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [ENV_PROVIDERS, APP_PROVIDERS],
})
export class AppModule {
  constructor(public appRef: ApplicationRef, public appState: AppState) {}

  public hmrOnInit(store: StoreType) {
    if (!store || !store.state) {
      return;
    }
    console.log("HMR store", JSON.stringify(store, null, 2));
    /**
     * Set state
     */
    this.appState._state = store.state;
    /**
     * Set input values
     */
    if ("restoreInputValues" in store) {
      let restoreInputValues = store.restoreInputValues;
      setTimeout(restoreInputValues);
    }

    this.appRef.tick();
    delete store.state;
    delete store.restoreInputValues;
  }

  public hmrOnDestroy(store: StoreType) {
    const cmpLocation = this.appRef.components.map(
      (cmp) => cmp.location.nativeElement
    );
    /**
     * Save state
     */
    const state = this.appState._state;
    store.state = state;
    /**
     * Recreate root elements
     */
    store.disposeOldHosts = createNewHosts(cmpLocation);
    /**
     * Save input values
     */
    store.restoreInputValues = createInputTransfer();
    /**
     * Remove styles
     */
    removeNgStyles();
  }

  public hmrAfterDestroy(store: StoreType) {
    /**
     * Display new elements
     */
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }
}



// WEBPACK FOOTER //
// node_modules/ngc-webpack??ref--0-3!./~/angular2-template-loader!./src/app/app.module.ts